import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CalendarClock, CheckCircle, Loader2, Scissors, XCircle } from 'lucide-react';
import api from '../utils/api';
import { formatDateDisplay } from '../utils/helpers';

export default function AppointmentManagePage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ data: '', horario: '' });

  const loadAppointment = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/barbearia/appointment/${id}/manage`, { params: { token } });
      setData(response.data);
      setForm({
        data: response.data.appointment.data || '',
        horario: response.data.appointment.horario || ''
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Nao foi possivel carregar o agendamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAppointment();
  }, [id, token]);

  const reschedule = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post(`/barbearia/appointment/${id}/reschedule`, { token, ...form });
      setSuccess('Agendamento remarcado com sucesso.');
      await loadAppointment();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao remarcar');
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    if (!confirm('Tem certeza que deseja cancelar este agendamento?')) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post(`/barbearia/appointment/${id}/cancel`, { token });
      setSuccess('Agendamento cancelado com sucesso.');
      await loadAppointment();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao cancelar');
    } finally {
      setSaving(false);
    }
  };

  const appointment = data?.appointment;
  const publicUrl = appointment?.barbershop?.slug ? `/b/${appointment.barbershop.slug}` : '/';

  return (
    <div className="min-h-screen bg-dark-900 text-white px-4 py-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <Link to={publicUrl} className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white">
          <ArrowLeft className="w-4 h-4" />
          Voltar para a pagina da barbearia
        </Link>

        <div className="card overflow-hidden border-primary-500/20">
          <div className="p-6 sm:p-8 bg-gradient-to-br from-primary-950/40 to-dark-800 border-b border-dark-600">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary-600 flex items-center justify-center">
                <Scissors className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-primary-300">{appointment?.barbershop?.nome || 'BarberFlow'}</p>
                <h1 className="text-2xl font-bold">Gerenciar agendamento</h1>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            {loading ? (
              <div className="flex items-center gap-2 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                Carregando...
              </div>
            ) : error && !appointment ? (
              <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-4 text-red-300">{error}</div>
            ) : (
              <div className="space-y-6">
                {error && <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-4 text-red-300">{error}</div>}
                {success && <div className="rounded-xl border border-green-500/20 bg-green-500/10 p-4 text-green-300">{success}</div>}

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="rounded-xl bg-dark-700/60 border border-dark-600 p-4">
                    <p className="text-sm text-gray-400">Cliente</p>
                    <p className="font-semibold">{appointment.nome_cliente}</p>
                  </div>
                  <div className="rounded-xl bg-dark-700/60 border border-dark-600 p-4">
                    <p className="text-sm text-gray-400">Servico</p>
                    <p className="font-semibold">{appointment.servico_nome || 'Servico'}</p>
                  </div>
                  <div className="rounded-xl bg-dark-700/60 border border-dark-600 p-4">
                    <p className="text-sm text-gray-400">Data atual</p>
                    <p className="font-semibold">{formatDateDisplay(appointment.data)} as {appointment.horario}</p>
                  </div>
                  <div className="rounded-xl bg-dark-700/60 border border-dark-600 p-4">
                    <p className="text-sm text-gray-400">Status</p>
                    <p className="font-semibold capitalize">{appointment.status}</p>
                  </div>
                </div>

                {!data.can_change && (
                  <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/10 p-4 text-yellow-200 text-sm">
                    A politica desta barbearia permite alteracoes somente ate {data.cancellation_policy_hours}h antes do horario.
                  </div>
                )}

                <form onSubmit={reschedule} className="grid sm:grid-cols-[1fr_150px_auto] gap-3 items-end">
                  <div>
                    <label className="label">Nova data</label>
                    <input type="date" className="input" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} disabled={!data.can_change || saving} />
                  </div>
                  <div>
                    <label className="label">Horario</label>
                    <input type="time" className="input" value={form.horario} onChange={(e) => setForm({ ...form, horario: e.target.value })} disabled={!data.can_change || saving} />
                  </div>
                  <button className="btn-primary" disabled={!data.can_change || saving}>
                    <CalendarClock className="w-4 h-4 mr-2" />
                    Remarcar
                  </button>
                </form>

                <button onClick={cancel} disabled={!data.can_change || saving || appointment.status === 'cancelado'} className="btn-outline w-full border-red-500/40 text-red-300 hover:border-red-400 hover:text-red-200">
                  {appointment.status === 'cancelado' ? <CheckCircle className="w-4 h-4 mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                  {appointment.status === 'cancelado' ? 'Agendamento cancelado' : 'Cancelar agendamento'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
